import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Send, Timer } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Empty, Field, PlainSelect } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Stars, StarInput } from "../components/Stars";

const JOB = { offered: ["Waiting for reply", "bg-amber-100 text-amber-800"], accepted: ["Accepted", "bg-emerald-100 text-emerald-800"],
  unassigned: ["Needs a practitioner", "bg-red-100 text-red-700"], reassigning: ["Reassigning", "bg-muted"] };

function JobSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/home/job-settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = () => api.put("/home/job-settings", { ...s, timeout_hours: Number(s.timeout_hours) }).then(() => toast.success("Saved")).catch((e) => toast.error(errMsg(e)));
  const check = () => api.post("/home/jobs/check-timeouts").then((r) => toast.success(`${r.data.reassigned} overdue job(s) passed on`)).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap items-end gap-4" data-testid="job-settings">
      <Field label="Practitioner must reply within (hours)"><Input type="number" min={0.25} step={0.25} className="w-28" data-testid="job-timeout-input" value={s.timeout_hours} onChange={(e) => setS({ ...s, timeout_hours: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm pb-2"><Switch data-testid="job-auto-switch" checked={s.auto_reassign} onCheckedChange={(v) => setS({ ...s, auto_reassign: v })} />Automatically offer to the next eligible practitioner</label>
      <Button className="rounded-full" data-testid="job-settings-save" onClick={save}>Save</Button>
      <Button variant="outline" className="rounded-full" data-testid="job-check-timeouts" onClick={check}><Timer className="h-4 w-4 mr-1" />Check overdue now</Button>
    </div>
  );
}

function JobRow({ j, pros, act }) {
  const { user, can } = useApp();
  const [label, tone] = JOB[j.job_status] || ["Awaiting payment", "bg-muted"];
  const mine = j.professional_id === user.id || user.role === "owner";
  const live = j.status !== "pending_deposit";
  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap items-center gap-3 text-sm" data-testid={`job-${j.id}`}>
      <div className="flex-1 min-w-[220px]"><div className="font-semibold">{j.date} · {j.start_time} · {j.service_name}</div>
        <div className="text-xs text-muted-foreground">{j.client_name} · {j.postcode} · with <b>{j.professional_name}</b></div></div>
      <span className={`text-xs rounded-full px-2 py-0.5 ${tone}`} data-testid={`job-status-${j.id}`}>{label}</span>
      {mine && live && ["offered", "unassigned"].includes(j.job_status) && <Button size="sm" className="rounded-full" data-testid={`job-accept-${j.id}`} onClick={() => act(`/home/jobs/${j.id}/respond`, { accept: true }, "Accepted")}><Check className="h-4 w-4 mr-1" />Accept</Button>}
      {mine && live && j.job_status === "offered" && <Button size="sm" variant="ghost" data-testid={`job-decline-${j.id}`} onClick={() => act(`/home/jobs/${j.id}/respond`, { accept: false }, "Declined – passed on")}><X className="h-4 w-4 mr-1" />Decline</Button>}
      {can("agenda.write") && live && <Button size="sm" variant="outline" className="rounded-full" data-testid={`job-resend-${j.id}`} onClick={() => act(`/home/jobs/${j.id}/resend`, {}, "Alert sent")}><Send className="h-4 w-4 mr-1" />Resend alert</Button>}
      {pros.length > 0 && <PlainSelect data-testid={`job-assign-${j.id}`} className="!w-44" value="" onChange={(e) => e.target.value && act(`/home/jobs/${j.id}/assign`, { user_id: e.target.value }, "Reassigned")}>
        <option value="">Assign to…</option>{pros.filter((p) => p.user_id !== j.professional_id).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}</PlainSelect>}
    </div>
  );
}

export function Jobs() {
  const { can } = useApp();
  const [jobs, setJobs] = useState([]);
  const [pros, setPros] = useState([]);
  const team = can("staff.read") && can("agenda.write");
  const load = useCallback(() => {
    api.get("/home/jobs").then((r) => setJobs(r.data));
    if (team) api.get("/home/pros").then((r) => setPros(r.data.filter((p) => p.active)));
  }, [team]);
  useEffect(() => { load(); }, [load]);
  const act = (url, body, msg) => api.post(url, body).then(() => { toast.success(msg); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Each new home visit is sent to the chosen practitioner by email and WhatsApp with one-tap Accept / Decline. If they decline or don't reply in time it goes to the next eligible practitioner, and the patient can pick someone else.</p>
      {can("settings.write") && <JobSettings />}
      {!jobs.length && <Empty text="No upcoming home visits" testId="jobs-empty" />}
      {jobs.map((j) => <JobRow key={j.id} j={j} pros={team ? pros : []} act={act} />)}
    </div>
  );
}

function FeedbackForm({ v, onDone }) {
  const [f, setF] = useState({ stars: 0, comment: "", share_with_patient: false });
  const send = () => api.post(`/home/appointments/${v.id}/feedback`, f).then(() => { toast.success("Feedback saved"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 space-y-2 text-sm" data-testid={`feedback-form-${v.id}`}>
      <div className="font-semibold">{v.client_name} <span className="font-normal text-muted-foreground">· {v.service_name} · {v.date} · {v.professional_name}</span></div>
      <StarInput value={f.stars} onChange={(s) => setF({ ...f, stars: s })} testId={`feedback-star-${v.id}`} />
      <Textarea rows={2} data-testid={`feedback-comment-${v.id}`} placeholder="Feedback about this patient (punctuality, home setting, aftercare advice…)" value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2"><Switch data-testid={`feedback-share-${v.id}`} checked={f.share_with_patient} onCheckedChange={(s) => setF({ ...f, share_with_patient: s })} />Share the comment with the patient</label>
        <Button size="sm" className="rounded-full" data-testid={`feedback-submit-${v.id}`} disabled={!f.stars} onClick={send}>Save feedback</Button>
      </div>
    </div>
  );
}

function Summary({ ratings }) {
  const by = {};
  ratings.forEach((r) => { by[r.professional_name] = [...(by[r.professional_name] || []), r.stars]; });
  return (
    <div className="flex flex-wrap gap-2" data-testid="ratings-summary">{Object.entries(by).map(([n, s]) => (
      <div key={n} className="rounded-lg border bg-card px-3 py-2 text-sm flex items-center gap-2"><b>{n}</b><Stars value={s.reduce((a, b) => a + b, 0) / s.length} />{(s.reduce((a, b) => a + b, 0) / s.length).toFixed(1)} <span className="text-muted-foreground">({s.length})</span></div>))}</div>
  );
}

export function Ratings() {
  const { can } = useApp();
  const [ratings, setRatings] = useState([]);
  const [fb, setFb] = useState({ due: [], given: [] });
  const load = useCallback(() => {
    api.get("/home/ratings").then((r) => setRatings(r.data));
    api.get("/home/feedback").then((r) => setFb(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const hide = (r, v) => api.patch(`/home/ratings/${r.id}`, { comment_hidden: v }).then(load).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <section className="space-y-3"><h3 className="text-lg">Patients rating practitioners</h3>
        <Summary ratings={ratings} />
        {!ratings.length && <Empty text="No ratings yet" testId="ratings-empty" />}
        {ratings.map((r) => (
          <div key={r.id} className="rounded-[var(--radius)] border bg-card p-4 space-y-1 text-sm" data-testid={`rating-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2"><Stars value={r.stars} /><b>{r.professional_name}</b><span className="text-muted-foreground">· {r.client_short} · {r.service_name} · {r.created_at.slice(0, 10)}</span></div>
            {r.comment && <p className={r.comment_hidden ? "line-through text-muted-foreground" : ""}>{r.comment}</p>}
            {r.comment && can("settings.write") && <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch data-testid={`rating-hide-${r.id}`} checked={!!r.comment_hidden} onCheckedChange={(v) => hide(r, v)} />Hide comment from the public page</label>}
          </div>))}
      </section>
      <section className="space-y-3"><h3 className="text-lg">Practitioner feedback for patients</h3>
        {!fb.due.length && <Empty text="No completed home visits waiting for feedback" testId="feedback-due-empty" />}
        {fb.due.map((v) => <FeedbackForm key={v.id} v={v} onDone={load} />)}
        {fb.given.map((f) => (
          <div key={f.id} className="rounded-lg border bg-card p-3 text-sm space-y-1" data-testid={`feedback-${f.id}`}>
            <div className="flex flex-wrap items-center gap-2"><Stars value={f.stars} /><b>{f.client_name}</b><span className="text-muted-foreground">· by {f.professional_name} · {f.date}</span>{f.share_with_patient && <span className="text-xs rounded-full bg-primary/10 text-primary px-2">shared</span>}</div>
            {f.comment && <p className="text-muted-foreground">{f.comment}</p>}
          </div>))}
      </section>
    </div>
  );
}
